import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { API } from 'api';
import axios, { AxiosError, HttpStatusCode } from 'axios';
import { getSSOCallbackOverrideUrl } from 'features/sso/utils';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { User } from 'shared/types';

type UseSSOCallbackResponse = {
  key: string;
  user: User;
};

type UseSSOCallbackProps = {
  params: Record<string, string | null>;
  onSuccess: (data: UseSSOCallbackResponse) => void;
  onError: (message: string) => void;
};

export type UseSSOCallback = Omit<
  UseQueryResult<UseSSOCallbackResponse>,
  'data'
>;

const fetchSSOCallback = async (
  params: Record<string, string | null>,
  t: TFunction,
  onSuccess: (data: UseSSOCallbackResponse) => void,
  onError: (message: string) => void,
): Promise<UseSSOCallbackResponse> => {
  if (params.error) {
    onError(t('login.errors.ssoLoginFailed'));
    throw new Error('Error provided in SSO callback parameters');
  }

  try {
    const response = await API.get(`organization/sso/callback/`, {
      params: {
        ...params,
        callbackUrl: getSSOCallbackOverrideUrl(),
      },
    });
    if (response.data?.key && response.data?.user) {
      onSuccess(response.data);
    }

    return response.data;
  } catch (error: unknown) {
    if (
      axios.isAxiosError(error) &&
      (error as AxiosError).response?.status === HttpStatusCode.Unauthorized
    ) {
      onError(t('login.errors.ssoLoginNoUser'));
    } else {
      onError(t('login.errors.ssoLoginFailed'));
    }
    throw error;
  }
};

export const useSSOCallback = (props: UseSSOCallbackProps): UseSSOCallback => {
  const { t } = useTranslation();
  const { params, onSuccess, onError } = props;
  const { data, ...results } = useQuery<UseSSOCallbackResponse>({
    queryKey: ['ssoCallback', params],
    queryFn: () => fetchSSOCallback(params, t, onSuccess, onError),
  });

  return {
    ...results,
  };
};
